import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";

const C = {
  bg: "#f5ead4",
  bgHeader: "#6b3a1f",
  text: "#3d1f0a",
  gold: "#8b6f3a",
  goldLight: "#c9a96e",
  border: "#d4c4a0",
  current: "#8b2500",
};

const Banner = ({ zmanimList = [], title, showTitle = true, hebrewDate, countdown }) => {
  const [now, setNow] = useState(new Date());
  const scrollRef = useRef(null);
  const trackRef = useRef(null);
  const [animDuration, setAnimDuration] = useState(0);
  const animatingRef = useRef(false);
  const uid = useMemo(() => `ban-${Math.random().toString(36).slice(2, 8)}`, []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const checkOverflow = useCallback(() => {
    if (!scrollRef.current || !trackRef.current) return;
    const containerW = scrollRef.current.clientWidth;
    const children = trackRef.current.children;
    if (children.length === 0) return;
    let totalW = 0;
    const count = animatingRef.current ? Math.ceil(children.length / 2) : children.length;
    for (let i = 0; i < count; i++) totalW += children[i].offsetWidth + 12;
    const shouldAnimate = totalW > containerW;
    animatingRef.current = shouldAnimate;
    setAnimDuration(shouldAnimate ? totalW / 40 : 0);
  }, []);

  useEffect(() => {
    checkOverflow();
    const raf = requestAnimationFrame(checkOverflow);
    if (!scrollRef.current) return;
    const ro = new ResizeObserver(checkOverflow);
    ro.observe(scrollRef.current);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [zmanimList, checkOverflow]);

  const hhmm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;

  const renderItems = (suffix = "") =>
    zmanimList.map((item) => {
      const toMinAdj = t => { const [h, m] = t.split(":").map(Number); return (h === 0 ? 24 : h) * 60 + m; };
          const isPast = toMinAdj(item.time) < toMinAdj(hhmm);
      const isCurrent = item.time === hhmm;
      return (
        <div key={item.key + suffix} style={{
          flexShrink: 0, display: "flex", alignItems: "center", gap: "8px",
          padding: "0.35rem 0.9rem",
          backgroundColor: isCurrent ? `${C.current}15` : "#fff",
          borderRadius: "20px",
          border: isCurrent ? `2px solid ${C.current}` : `1px solid ${C.border}`,
          opacity: isPast ? 0.65 : 1,
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        }}>
          <span style={{ color: isCurrent ? C.current : C.text, fontSize: "0.78em", fontWeight: 500, whiteSpace: "nowrap" }}>{item.label}</span>
          <span style={{ color: isCurrent ? C.current : C.gold, fontSize: "0.95em", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{item.time}</span>
        </div>
      );
    });

  return (
    <div style={{
      direction: "rtl", width: "100%", height: "100%",
      backgroundColor: C.bg, overflow: "hidden",
      display: "flex", flexDirection: "column", fontFamily: "inherit",
      borderTop: `2px solid ${C.goldLight}`, borderBottom: `2px solid ${C.goldLight}`,
      boxSizing: "border-box",
    }}>
      {showTitle && (
        <div style={{
          backgroundColor: C.bgHeader, padding: "0.35rem 1rem",
          display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0,
        }}>
          <span style={{ color: C.goldLight, fontWeight: 700, fontSize: "0.9em" }}>{title}</span>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {countdown && (
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ color: "#d4c4a8", fontSize: "0.72em" }}>{countdown.label}</span>
                <span style={{ color: "#ff9966", fontSize: "0.85em", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{countdown.remaining}</span>
              </div>
            )}
            <span style={{ color: "#d4c4a8", fontSize: "0.75em", opacity: 0.85 }}>{hebrewDate?.hebrewShort || ""}</span>
          </div>
        </div>
      )}

      {animDuration > 0 && (
        <style>{`@keyframes ${uid}{from{transform:translateX(0)}to{transform:translateX(50%)}}`}</style>
      )}

      <div ref={scrollRef} style={{ flex: 1, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: animDuration > 0 ? "flex-start" : "center", padding: "0.3rem 0.6rem" }}>
        <div ref={trackRef} style={{
          display: "flex", gap: "12px",
          animation: animDuration > 0 ? `${uid} ${animDuration}s linear infinite` : "none",
        }}>
          {renderItems()}
          {animDuration > 0 && renderItems("_dup")}
        </div>
      </div>
    </div>
  );
};

export default Banner;
